import {
  paymentConditions,
  tvaOptions,
  acquisitionOptions,
} from "./clientFormOptions";
import { useEffect, useRef, useState } from "react";
import { activityFromApe } from "../../utils/companyActivity";
import axiosInstance from "../../axios/AxiosInstance";
import { Label } from "../../components/reusableComponents/Label";
import { Input } from "../../components/reusableComponents/Input";
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectContent,
} from "../../components/reusableComponents/Select";
import { message } from "antd";
import { Button } from "../../components/reusableComponents/Button";
import { Loader2 } from "lucide-react";
import { freelanceCommercialName } from "../../utils/freelanceCommercial";

export default function ClientForm({ setClients, clients, close, onCreated }) {
  const [form, setForm] = useState({
    siret: "",
    denomination: "",
    companyName: "",
    pld: "",
    ape: "",
    activity: "",
    TVA: "",
    tvaRate: "",
    address: "",
    postCode: "",
    town: "",
    addressLivraison: "",
    postCodeLivraison: "",
    townLivraison: "",
    addressFacturation: "",
    postCodeFacturation: "",
    townFacturation: "",
    contactFirstname: "",
    contactLastname: "",
    contactEMail: "",
    contactPhone: "",
    facturation: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [lookup, setLookup] = useState({ status: "idle" });
  const [lookupAttempt, setLookupAttempt] = useState(0);
  const formRef = useRef(form);
  const autofilled = useRef({});
  formRef.current = form;
  const normalizedSiret = form.siret.replace(/\s/g, "");

  useEffect(() => {
    if (!/^\d{14}$/.test(normalizedSiret)) {
      setLookup({ status: "idle" });
      return;
    }
    const controller = new AbortController();
    let active = true;
    setLookup({ status: "loading" });
    const timer = setTimeout(async () => {
      const snapshot = { ...formRef.current };
      try {
        const { data } = await axiosInstance.get(
          `client/lookup/siret/${normalizedSiret}`,
          { signal: controller.signal },
        );
        if (
          !active ||
          formRef.current.siret.replace(/\s/g, "") !== normalizedSiret
        )
          return;
        setForm((current) => {
          if (current.siret.replace(/\s/g, "") !== normalizedSiret)
            return current;
          const next = { ...current };
          const applied = {};
          for (const field of [
            "denomination",
            "ape",
            "activity",
            "TVA",
            "address",
            "postCode",
            "town",
          ]) {
            // Preserve changes made while the registry request was in flight.
            if (data[field] && current[field] === snapshot[field]) {
              next[field] = data[field];
              applied[field] = data[field];
            }
          }
          autofilled.current = applied;
          return next;
        });
        setLookup({ status: "found", company: data });
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        setLookup({
          status: "error",
          message:
            error.response?.data?.message ||
            "Recherche indisponible. Réessayez ou complétez la fiche manuellement.",
        });
      }
    }, 500);
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [normalizedSiret, lookupAttempt]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const previousAutofill = autofilled.current;
    const changedSiret =
      name === "siret" && value.replace(/\s/g, "") !== normalizedSiret;
    if (changedSiret) {
      autofilled.current = {};
      setLookup({ status: "idle" });
    }
    setForm((current) => {
      const next = { ...current, [name]: value };
      if (name === "ape") next.activity = activityFromApe(value);
      if (changedSiret) {
        for (const [field, previous] of Object.entries(previousAutofill)) {
          if (next[field] === previous) next[field] = "";
        }
      }
      return next;
    });
  };

  const copyAddressTo = (target) => {
    setForm({
      ...form,
      [`address${target}`]: form.address,
      [`postCode${target}`]: form.postCode,
      [`town${target}`]: form.town,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!/^\d{14}$/.test(normalizedSiret)) {
      message.error("Le SIRET doit contenir 14 chiffres.");
      return;
    }
    const requiredFields = [
      "siret",
      "denomination",
      "ape",
      "address",
      "postCode",
      "town",
      "addressLivraison",
      "postCodeLivraison",
      "townLivraison",
      "addressFacturation",
      "postCodeFacturation",
      "townFacturation",
      "contactFirstname",
      "contactLastname",
      "contactEMail",
      "contactPhone",
      "facturation",
      "tvaRate",
    ];

    const emptyField = requiredFields.find((f) => !form[f]?.trim());
    console.log(emptyField);
    if (emptyField) {
      alert("Tous les champs sont obligatoires");
      return;
    }
    setIsLoading(true);

    try {
      const res = await axiosInstance.post("client", {
        ...form,
        siret: normalizedSiret,
        APE: form.ape,
        commercial: freelanceCommercialName(),
        companyName: form.companyName.trim() || form.denomination,
        legalName: form.denomination,
        contacts: [
          {
            firstname: form.contactFirstname,
            lastname: form.contactLastname,
            email: form.contactEMail,
            mobile: form.contactPhone.replace(/\s/g, ""),
          },
        ],
      });

      setClients([...clients, res.data]);
      message.success("Client créé");
      close();
      onCreated?.(res.data);
    } catch (e) {
      console.log(e);
      if (e.response?.data?.statusCode === 409) {
        message.error("Le client existe déjà !");
      } else {
        message.error("Erreur lors de la création du client");
      }
    }
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-4">
      {/* Informations Société */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="siret">SIRET</Label>
          <Input
            id="siret"
            name="siret"
            inputMode="numeric"
            aria-describedby="siret-lookup-status"
            placeholder="123 456 789 00000"
            value={form.siret}
            onChange={handleChange}
          />
          <div id="siret-lookup-status" className="text-sm" aria-live="polite">
            {lookup.status === "idle" && (
              <p className="text-slate-500">
                Saisissez les 14 chiffres pour rechercher l’entreprise dans le
                registre officiel.
              </p>
            )}
            {lookup.status === "loading" && (
              <p role="status">Recherche dans le registre officiel…</p>
            )}
            {lookup.status === "found" && (
              <div>
                <a
                  className="text-indigo-700 underline"
                  href={lookup.company.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Voir la fiche officielle de l’établissement
                </a>
                <p>
                  Informations disponibles préremplies. Vérifiez-les avant de
                  créer le client.
                </p>
                {!lookup.company.active && (
                  <p className="text-amber-700">
                    Cet établissement est indiqué comme fermé dans le registre.
                  </p>
                )}
                {!lookup.company.TVA && (
                  <p className="text-slate-500">
                    Numéro de TVA non communiqué par le registre.
                  </p>
                )}
              </div>
            )}
            {lookup.status === "error" && (
              <div>
                <p role="alert" className="text-amber-700">
                  {lookup.message}
                </p>
                <button
                  type="button"
                  className="text-indigo-700 underline"
                  onClick={() => setLookupAttempt((value) => value + 1)}
                >
                  Réessayer la recherche
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="companyName">Nom du client / établissement</Label>
          <Input
            id="companyName"
            name="companyName"
            placeholder="Ex. : Palais des femmes – Armée du salut"
            value={form.companyName}
            onChange={handleChange}
          />
          <p className="text-xs text-slate-500">
            Le nom affiché dans votre portefeuille. À défaut, la raison sociale
            sera utilisée.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="denomination">Raison Sociale</Label>
          <Input
            id="denomination"
            name="denomination"
            placeholder="Nom de l'entreprise"
            value={form.denomination}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pld">Numéro PLD</Label>
          <Input
            name="pld"
            placeholder="Numéro PLD"
            value={form.pld}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ape">Code APE</Label>
          <Input
            name="ape"
            placeholder="APE 1234Z"
            value={form.ape}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="activity">
            Activité principale de la société (NAF/APE)
          </Label>
          <Input
            id="activity"
            name="activity"
            placeholder="Libellé correspondant au code NAF/APE"
            value={form.activity}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="TVA">TVA Intra (si applicable)</Label>
          <Input
            name="TVA"
            placeholder="FR12345678901"
            value={form.TVA}
            onChange={handleChange}
          />
        </div>
      </div>

      {/* Adresse siège sur toute la largeur */}
      <div className="space-y-2 pt-2">
        <Label htmlFor="address">Adresse de l’établissement</Label>
        <Input
          name="address"
          placeholder="10 Rue Exemple"
          value={form.address}
          onChange={handleChange}
        />
      </div>

      {/* Code postal + ville */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="postCode">Code Postal</Label>
          <Input
            name="postCode"
            placeholder="75000"
            value={form.postCode}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="town">Ville</Label>
          <Input
            name="town"
            placeholder="Paris"
            value={form.town}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="flex  items-center justify-between">
        <div className="w-1/2 flex justify-center">
          <Button type="button" onClick={() => copyAddressTo("Livraison")}>
            Copier vers Livraison
          </Button>
        </div>
        <div className="w-1/2 flex justify-center">
          <Button type="button" onClick={() => copyAddressTo("Facturation")}>
            Copier vers Facturation
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Adresse Livraison</Label>
          <Input
            name="addressLivraison"
            placeholder="Adresse"
            value={form.addressLivraison}
            onChange={handleChange}
          />
          <Input
            name="postCodeLivraison"
            placeholder="Code postal"
            value={form.postCodeLivraison}
            onChange={handleChange}
          />
          <Input
            name="townLivraison"
            placeholder="Ville"
            value={form.townLivraison}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label>Adresse Facturation</Label>
          <Input
            name="addressFacturation"
            placeholder="Adresse"
            value={form.addressFacturation}
            onChange={handleChange}
          />
          <Input
            name="postCodeFacturation"
            placeholder="Code postal"
            value={form.postCodeFacturation}
            onChange={handleChange}
          />
          <Input
            name="townFacturation"
            placeholder="Ville"
            value={form.townFacturation}
            onChange={handleChange}
          />
        </div>
      </div>
      <div>
        <Label className="space-y-4">Contact principal</Label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nom</Label>
          <Input
            name="contactLastname"
            placeholder="Dupont"
            value={form.contactLastname}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label>Prénom</Label>
          <Input
            name="contactFirstname"
            placeholder="Jean"
            value={form.contactFirstname}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input
            name="contactEMail"
            type="email"
            placeholder="jean.dupont@email.com"
            value={form.contactEMail}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label>Téléphone</Label>
          <Input
            name="contactPhone"
            placeholder="0612345678"
            value={form.contactPhone}
            onChange={handleChange}
          />
        </div>
      </div>

      <div>
        <div className="space-y-2">
          <Label>Provenance</Label>
          <Select
            onValueChange={(val) =>
              setForm({ ...form, clientAcquisition: val })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {acquisitionOptions.map((cond) => (
                <SelectItem key={cond} value={cond}>
                  {cond}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="commercial">Commercial</Label>
        <Input
          id="commercial"
          name="commercial"
          readOnly
          value={freelanceCommercialName() || "Votre nom"}
        />
        <p className="text-xs text-slate-500">
          Le commercial de cette fiche est automatiquement votre nom.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Condition de paiement</Label>
          <Select
            onValueChange={(val) => setForm({ ...form, facturation: val })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {paymentConditions.map((cond) => (
                <SelectItem key={cond} value={cond}>
                  {cond}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>TVA</Label>
          <Select onValueChange={(val) => setForm({ ...form, tvaRate: val })}>
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {tvaOptions.map((rate) => (
                <SelectItem key={rate} value={rate}>
                  {rate}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="pt-4 flex justify-end gap-4">
        <Button
          type="submit"
          className="bg-indigo-600 text-white hover:bg-indigo-700"
        >
          {!isLoading && <span>Créer le client</span>}
          {isLoading && (
            <span>
              <Loader2 className="w-5 h-5 text-white" />
            </span>
          )}
        </Button>
      </div>
    </form>
  );
}
